import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/site";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/consultation", label: "Consultation" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const dark = location.pathname === "/" && !scrolled;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#E5E5E5]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo"
          className="flex items-center gap-3 group"
        >
          <img
            src="/logo.png"
            alt="Haven and Hue logo"
            className="h-9 w-auto md:h-10 object-contain shrink-0"
          />
          <span
            className={`font-serif-display text-xl md:text-2xl tracking-tight transition-colors duration-300 ${
              dark ? "text-[#FAF9F6]" : "text-[#1A1A1A]"
            }`}
          >
            Haven <span className="text-[#C5A880]">&</span> Hue
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => {
            const active = location.pathname === l.to;
            return (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`relative text-[13px] tracking-wide transition-colors duration-300 group ${
                  dark ? "text-[#FAF9F6]/85 hover:text-[#C5A880]" : "text-[#4A4A4A] hover:text-[#1A1A1A]"
                }`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px bg-[#C5A880] transition-all duration-300 ${
                    active ? "w-full" : "w-0 group-hover:w-full"
                  }`}
                />
              </Link>
            );
          })}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden ${dark ? "text-[#FAF9F6]" : "text-[#1A1A1A]"}`}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#FAF9F6] border-b border-[#E5E5E5]"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="font-serif-display text-2xl text-[#1A1A1A]"
                >
                  {l.label}
                </Link>
              ))}
              <a href={`tel:${BRAND.phones[0]}`} className="overline text-[#8B7355] mt-2">
                {BRAND.phones[0]}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
